import React from "react";
import { Tag } from "@jem-open/jem-ui";
import { RosterConfig } from "../services/model";

export interface RosterAccordionSummaryProps {
  roster: RosterConfig;
}

/**
 * Header summary content for a Roster's Accordion trigger.
 *
 * Renders the at-a-glance Roster identity (name + shift label + assigned count)
 * plus the "Unsaved" Tag while the Roster is dirty, so admins can spot drift
 * without expanding every row.
 *
 * Note: the prototype rendered a `<span role="button">` "Move Roster" control
 * INSIDE the AccordionTrigger (a button-inside-a-button a11y violation). On port
 * the Move Roster control is lifted out to a real `Button` rendered as a sibling
 * of the AccordionTrigger by the page — see `SiteConfigurationPage`.
 */
export const RosterAccordionSummary: React.FC<RosterAccordionSummaryProps> = ({
  roster,
}) => {
  const hasShifts = roster.shiftTypes && roster.shiftTypes.length > 0;
  const shiftTypes = roster.shiftTypes ?? [];
  const totalAssigned = hasShifts
    ? shiftTypes.reduce((acc, s) => acc + s.assignedHeadcount, 0)
    : roster.assignedHeadcount;
  const shiftLabel = hasShifts
    ? shiftTypes.map((s) => s.type).join(" · ")
    : roster.shiftWindow;

  return (
    <div className="flex flex-1 flex-wrap items-center justify-between gap-sm pr-sm">
      <div className="text-left">
        <p className="font-semibold text-greyscale-text-title">{roster.name}</p>
        <p className="text-xs text-greyscale-text-caption">
          {shiftLabel} · {totalAssigned} assigned
        </p>
      </div>
      {roster.dirty && (
        <Tag variant="pending" size="sm">
          Unsaved
        </Tag>
      )}
    </div>
  );
};

export default RosterAccordionSummary;
